import { useEffect, useState, useCallback } from 'react';
import { Card, CardBody, CardHeader, Modal, Row, Col, Input, Button,Badge } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../helpers/api';
import FeatherIcon from 'feather-icons-react';
import { handleApiResponse, createNotificationFromApiResponse, formatCreatedDate, formatDateTimeForAPI, workstatusOptions } from '../constants/utils';
import AssetUsageStatusFields from '../components/AssetUsageStatusFields';
import WorkOrderDetailsModal from '../components/WorkOrderDetailsModal';
import AssetModal from '../components/AssetModal';
import { getPresignedUrl } from '../helpers/fileUpload';

// Import new hooks and components
import { useReferenceData, usePagination, useNotification, useModalState } from '../Hooks/index.js'
import { NotificationAlert, LoadMoreButton, ConfirmDeleteModal } from '../components/Common/index.js';
import PermissionGuard from '../components/Common/PermissionGuard';
import { useTranslation } from 'react-i18next';

const Routine = () => {
    const { t } = useTranslation();
    document.title = `${t("shared.routine", "Routine")} | ArcMedix`;

    // User context
    const user = JSON.parse(localStorage.getItem('authUser'));
    const organizationId = user?.activeOrganizationId ?? null;

    // State
    const [routineRequests, setRoutineRequests] = useState([]);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Asset selection state
    const [assets, setAssets] = useState([]);
    const [assetSearchTerm, setAssetSearchTerm] = useState("");
    const [showAssetDropdown, setShowAssetDropdown] = useState(false);
    const [selectedAssetDisplay, setSelectedAssetDisplay] = useState("");
    const [assetFilterModal, setAssetFilterModal] = useState(false);
    const [assetFilters, setAssetFilters] = useState({
        location: '',
        manufacturer: ''
    });

    // Image modal state
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [currentImages, setCurrentImages] = useState([]);
    const [imageType, setImageType] = useState('pre'); // 'pre' or 'post'

    // Asset modal state
    const [assetModal, setAssetModal] = useState({
        isOpen: false,
        userData: null,
        loading: false
    });

    // Use new hooks
    const { locations, manufacturers, technicians } = useReferenceData(organizationId, {
        fetchLocations: true,
        fetchDepartments: false,
        fetchManufacturers: true,
        fetchTechnicians: true,
        fetchTools: false
    });

    const { count, hasMore, setHasMore, handleLoadMore: onLoadMore } = usePagination(10, 10);

    const { notification, showSuccess, showError } = useNotification(5000);

    const addEditModal = useModalState();
    const deleteModal = useModalState();
    const workOrderModal = useModalState();

    // Fetch routine requests
    const fetchRoutineRequests = async () => {
        try {
            setLoading(true);
            const searchParam = searchTerm.trim() ? `&search=${encodeURIComponent(searchTerm)}` : '';
            const { data } = await api.get(`routine-requests/getAllRoutineRequest?organizationId=${organizationId || 1}&count=${count}${searchParam}`);
            setRoutineRequests(data?.response || []);
            setHasMore((data?.response?.length || 0) === count);
        } catch (error) {
            console.error('Error fetching routine requests:', error);
            setApiError(t("business.routine.failed_fetch_data", 'Failed to fetch routine requests data'));
            setRoutineRequests([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch assets with filters
    const fetchAssets = async (searchTerm = '') => {
        try {
            const params = {
                organizationId: organizationId,
                count: 9999,
                search: searchTerm,
                currentLocation: assetFilters.location,
                manufacturer: assetFilters.manufacturer,
                hierarchy: false
            };
            const { data } = await api.get(`asset/all`, { params });
            setAssets(data?.response[0]?.assets || []);
        } catch (error) {
            console.error('Error fetching assets:', error);
            setAssets([]);
        }
    };

    // Search handler
    const handleSearch = useCallback((e) => {
        setSearchTerm(e.target.value);
    }, []);

    // Effect to fetch data when count or searchTerm changes
    useEffect(() => {
        fetchRoutineRequests();
    }, [count, searchTerm]);

    // Effect to fetch assets when search term or filters change
    useEffect(() => {
        if (organizationId && assetSearchTerm.length > 0) {
            fetchAssets(assetSearchTerm);
        } else if (organizationId && assetSearchTerm.length === 0) {
            setAssets([]);
        }
    }, [organizationId, assetSearchTerm, assetFilters]);

    const handleAdd = () => {
        setSelectedEntry({
            title: '',
            assetId: '',
            description: '',
            status: 'PENDING',
            comments: '',
            assignedTechnicians: [],
            preImages: [],
            postImages: [],
            usageStatus: '',
            downtimeReasonId: null
        });
        setErrors({});
        setApiError('');
        setAssetSearchTerm("");
        setSelectedAssetDisplay("");
        addEditModal.open();
    };

    // Image upload handlers
    const handleFileChange = async (e, imageType) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        try {
            const uploadedUrls = [];

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const publicURL = await getPresignedUrl(file);
                if (publicURL) {
                    uploadedUrls.push(publicURL);
                }
            }

            setSelectedEntry(prev => ({
                ...prev,
                [imageType]: [...(prev?.[imageType] || []), ...uploadedUrls]
            }));

        } catch (err) {
            setApiError(t("shared.error_during_upload", "Error during upload: ") + err.message);
        }
    };

    const removeImage = (indexToRemove, imageType) => {
        setSelectedEntry(prev => ({
            ...prev,
            [imageType]: prev?.[imageType]?.filter((_, index) => index !== indexToRemove) || []
        }));
    };

    const handleEdit = (entry) => {
        // Convert assignedTechnicianIds to assignedTechnicians for editing
        const assignedTechnicians = entry.assignedTechnicianIds ?
            entry.assignedTechnicianIds.map(techData =>
                typeof techData === 'object' ? techData : technicians.find(tech => tech.id === techData)
            ).filter(Boolean) : [];

        // Handle both assetId and asset properties
        const assetIdValue = entry.assetId?.id || entry.asset?.id || entry.assetId || '';

        setSelectedEntry({
            ...entry,
            title: entry.title || '',
            asset: entry.asset || entry.assetId || null,
            assetId: assetIdValue,
            description: entry.description || '',
            status: entry.status || 'PENDING',
            comments: entry.comments || '',
            assignedTechnicians,
            checkLists: entry.checkLists || [],
            preImages: entry.preImages || [],
            postImages: entry.postImages || [],
            usageStatus: entry.usageStatus || '',
            downtimeReasonId: entry.downtimeReasonId || null
        });
        // If editing, show the selected asset info
        if (entry.asset) {
            setSelectedAssetDisplay(`${entry.asset.assetName} - ${entry.asset.serialNumber}`);
        } else if (entry.assetId?.assetName) {
            setSelectedAssetDisplay(`${entry.assetId.assetName} - ${entry.assetId.serialNumber}`);
        } else {
            setSelectedAssetDisplay("");
        }
        setAssetSearchTerm("");
        setErrors({});
        setApiError('');
        addEditModal.open();
    };

    const handleAssetSelect = (asset) => {
        setSelectedEntry({
            ...selectedEntry,
            asset: asset,
            assetId: asset.id,
            usageStatus: asset.assetStatus || ''
        });
        setSelectedAssetDisplay(`${asset.assetName} - ${asset.serialNumber}`);
        setAssetSearchTerm("");
        setShowAssetDropdown(false);
        // Clear any existing error for assetId
        if (errors.assetId) {
            setErrors(prev => ({...prev, assetId: ''}));
        }
    };

    const handleAssetSearchChange = (e) => {
        const value = e.target.value;
        setAssetSearchTerm(value);
        setShowAssetDropdown(value.length > 0);
        if (value.length > 0) {
            fetchAssets(value);
        }
        if (value.length === 0) {
            setSelectedEntry({
                ...selectedEntry,
                asset: null,
                assetId: '',
                usageStatus: ''
            });
            setSelectedAssetDisplay("");
        }
    };

    const handleDelete = async () => {
        if (!deleteModal.data) return;

        try {
            await api.delete(`routine-requests/deleteRoutineRequest?id=${deleteModal.data.id}`);
            showSuccess(t("business.routine.deleted_successfully", 'Routine request deleted successfully'));
            fetchRoutineRequests();
            deleteModal.close();
        } catch (error) {
            console.error('Error deleting routine request:', error);
            showError(t("business.routine.failed_to_delete", 'Failed to delete routine request'));
        }
    };

    const handleWorkOrderClick = (workOrder) => {
        workOrderModal.open({ ...workOrder, type: 'routine' });
    };

    const handleAssetClick = async (assetId, cachedAsset = null) => {
        if (!assetId) return;

        setAssetModal(prev => ({
            ...prev,
            isOpen: true,
            userData: null,
            loading: true
        }));

        try {
            const { data: assetData } = await api.get(`asset/get-by-id?id=${assetId}`);
            const params = {
                assetId: assetId,
                count: 5
            };
            const { data: issuesData } = await api.get(`asset-issues/organization/filtered`, { params });

            setAssetModal(prev => ({
                ...prev,
                userData: {
                    ...assetData.response,
                    recentTickets: issuesData?.response || []
                },
                loading: false
            }));
        } catch (error) {
            console.error('Error fetching asset details:', error);
            if (cachedAsset) {
                setAssetModal({ isOpen: true, userData: { ...cachedAsset, record: false }, loading: false });
            } else {
                setAssetModal(prev => ({ ...prev, loading: false }));
            }
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setErrors({});
        setApiError('');

        const data = {
            title: selectedEntry?.title || '',
            dueDate: selectedEntry?.dueDate || '',
            assetId: selectedEntry?.assetId || null,
            description: selectedEntry?.description || '',
            status: selectedEntry?.status || 'PENDING',
            comments: selectedEntry?.comments || '',
            assignedTechnicianIds: selectedEntry?.assignedTechnicians?.map(tech => tech.id) || [],
            preImages: selectedEntry?.preImages || [],
            postImages: selectedEntry?.postImages || [],
            usageStatus: selectedEntry?.usageStatus || '',
            downtimeReasonId: selectedEntry?.usageStatus === 'OFFLINE' ? (selectedEntry?.downtimeReasonId || null) : null
        };

        // Add checkLists for both create and update
        if (selectedEntry?.checkLists) {
            data.checkLists = selectedEntry.checkLists.filter(item => item.checkListName.trim() !== '');
        }

        // Add id for update requests
        if (selectedEntry?.id) {
            data.id = selectedEntry.id;
        }

        // Validation
        const newErrors = {};
        if (!selectedEntry?.assetId) {
            newErrors.assetId = t("shared.asset_is_required", 'Asset is required');
        }
        if (!selectedEntry?.assignedTechnicians || selectedEntry.assignedTechnicians.length === 0) {
            newErrors.assignedTechnicianIds = t("shared.at_least_one_technician_must_be_assigned", 'At least one technician must be assigned');
        }
        if (selectedEntry?.assetId && !selectedEntry?.usageStatus) {
            newErrors.usageStatus = t("shared.usage_status_is_required", 'Usage status is required');
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            let response;
            if (selectedEntry?.id) {
                // Update
                response = await api.put(`routine-requests/updateRoutineRequest`, data);
            } else {
                // Create
                response = await api.post('routine-requests/createRoutineRequest', data);
            }

            const result = handleApiResponse(response);
            const notificationData = createNotificationFromApiResponse(result);

            if (result.success) {
                showSuccess(notificationData.message);
                addEditModal.close();
                fetchRoutineRequests();
            } else {
                showError(notificationData.message);
            }
        } catch (error) {
            console.error('Error saving routine request:', error);
            setApiError(t("business.routine.failed_to_save", 'Failed to save routine request'));
        }
    };

    // Image modal handlers
    const openImageModal = (images, index = 0, type = 'pre') => {
        setCurrentImages(images || []);
        setSelectedImageIndex(index);
        setImageType(type);
        setShowImageModal(true);
    };

    const closeImageModal = () => {
        setShowImageModal(false);
        setCurrentImages([]);
        setSelectedImageIndex(0);
        setImageType('pre');
    };

    const nextImage = () => {
        if (currentImages.length > 0) {
            setSelectedImageIndex((prev) =>
                prev === currentImages.length - 1 ? 0 : prev + 1
            );
        }
    };

    const prevImage = () => {
        if (currentImages.length > 0) {
            setSelectedImageIndex((prev) =>
                prev === 0 ? currentImages.length - 1 : prev - 1
            );
        }
    };

    useEffect(() => {
        fetchRoutineRequests();
    }, []);

    useEffect(() => {
        if (organizationId && assetSearchTerm.length > 0) {
            fetchAssets(assetSearchTerm);
        } else if (organizationId && assetSearchTerm.length === 0) {
            setAssets([]);
        }
    }, [organizationId, assetSearchTerm, assetFilters]);

    return (
        <div className='page-content mt-4'>
            <NotificationAlert
                type={notification.type}
                message={notification.message}
                animated
            />

            <div className="mb-4 d-flex align-items-center gap-3">
                <motion.div
                    className="mb-4 d-flex align-items-center gap-3"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <PermissionGuard permission="maintenance:create">
                        <button
                            type='button'
                            className='btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake'
                            onClick={handleAdd}
                        >
                            <FeatherIcon icon='plus' />
                            {t("business.routine.add_routine_request", "Add Routine Request")}
                        </button>
                    </PermissionGuard>
                    <div className="ms-auto" style={{ minWidth: '400px', flexGrow: 1, maxWidth: '500px' }}>
                        <Input
                            type='text'
                            placeholder={t("business.routine.search_placeholder", "Search Routine Requests...")}
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                </motion.div>
            </div>

            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="card-title text-success mb-0">{t("business.routine.routine_requests", "Routine Requests")}</h3>
                    </div>
                </CardHeader>

                <CardBody>
                    {apiError && (
                        <NotificationAlert type="error" message={apiError} />
                    )}

                    <div
                        className="table-responsive"
                        style={{
                            overflowX: 'auto',
                            whiteSpace: 'nowrap',
                            maxHeight: "calc(100vh - 300px)",
                            overflowY: "auto",
                        }}
                    >
                        <table className="table table-hover mb-0" style={{
                            borderSpacing: '0.1rem',
                            borderCollapse: 'separate'
                        }}>
                            <thead
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    background: "white",
                                    zIndex: 1,
                                }}>
                                <tr>
                                    <th>#</th>
                                    <th>{t("shared.ticket_number", "Ticket Number")}</th>
                                    <th>{t("shared.asset", "Asset")}</th>
                                    <th>{t("shared.due_date", "Due Date")}</th>
                                    <th>{t("shared.status", "Status")}</th>
                                    <th>{t("shared.assigned_technicians", "Assigned Technicians")}</th>
                                    <th>{t("shared.delete", "Delete")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                {routineRequests?.map((request, ind) => (
                                    <motion.tr
                                        key={request?.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <th scope="row">{ind+1}</th>
                                        <td>
                                            {request.requestId ? (
                                                <span
                                                    className="text-primary fw-medium"
                                                    role="button"
                                                    onClick={() => handleWorkOrderClick(request)}
                                                    style={{ cursor: 'pointer' }}
                                                    title={t("shared.click_view_details", "Click to view details")}
                                                >
                                                    {request.requestId}
                                                </span>
                                            ) : (
                                                <span className="text-muted">N/A</span>
                                            )}
                                        </td>
                                        <td>
                                            <span
                                                className="text-primary cursor-pointer hover-underline"
                                                onClick={() => handleAssetClick(request?.asset?.id, request?.asset)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {request?.asset?.assetName || t("shared.n_a", "N/A")}
                                            </span>
                                        </td>
                                          <td>{formatCreatedDate(request?.dueDate) || t("shared.n_a", "N/A")}</td>
                                            <td>
                                            {request.status ? (
                                                (() => {
                                                    const option = workstatusOptions(t).find(opt => opt.value === request.status) || {
                                                        label: request.status,
                                                        icon: 'help-circle',
                                                        colorClass: 'bg-secondary text-white',
                                                        borderColor: '#6c757d',
                                                    };
                                                    return (
                                                        <span
                                                            className={`badge rounded-pill py-1 px-2 d-inline-flex align-items-center gap-1 ${option.colorClass}`}
                                                            style={{ border: `1px solid ${option.borderColor}` }}
                                                        >
                                                            <FeatherIcon icon={option.icon} size={12} />
                                                            {option.label}
                                                        </span>
                                                    );
                                                })()
                                            ) : (
                                                <span className="text-muted">N/A</span>
                                            )}
                                            </td>
                                        <td className="align-middle">
                                            {request?.assignedTechnicianIds && request.assignedTechnicianIds.length > 0 ? (
                                                <div className="d-flex flex-wrap gap-1 align-items-center">
                                                    {request.assignedTechnicianIds.map((technician, index) => (
                                                        <span
                                                            key={technician.id || index}
                                                            className="badge bg-primary text-white"
                                                            style={{ fontSize: '10px' }}
                                                        >
                                                            <FeatherIcon icon="user" size={10} className="me-1" />
                                                            {technician.name || t("shared.n_a", "N/A")}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-muted small">{t("shared.not_assigned", "Not assigned")}</span>
                                            )}
                                        </td>
                                        <td>
                                            <PermissionGuard permission="maintenance:delete">
                                                <span className='text-danger' role='button' onClick={() => deleteModal.open(request)}>
                                                    <FeatherIcon icon="trash-2" />
                                                </span>
                                            </PermissionGuard>
                                        </td>
                                    </motion.tr>
                                ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>

                    {routineRequests.length === 0 && !loading && (
                        <div className="text-center py-4">
                            <p className="text-muted">{t("business.routine.no_routine_requests_found", "No routine requests found")}</p>
                        </div>
                    )}

                    <LoadMoreButton
                        hasMore={hasMore}
                        loading={loading}
                        onLoadMore={onLoadMore}
                        dataLength={routineRequests.length}
                        loadMoreText={t("business.routine.load_more", "Load More Routine Requests")}
                    />
                </CardBody>
            </Card>

            {/* Add/Edit Modal */}
            <Modal isOpen={addEditModal.isOpen} toggle={addEditModal.close} size="lg">
                <div className="modal-header">
                    <h5 className="modal-title">
                        {selectedEntry?.id ? t("business.routine.edit_routine_request", 'Edit Routine Request') : t("business.routine.add_routine_request", 'Add Routine Request')}
                    </h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={addEditModal.close}
                        aria-label={t("shared.close", "Close")}
                    />
                </div>
                <form onSubmit={handleSave}>
                    <div className="modal-body">
                        {errors.general && (
                            <NotificationAlert type="error" message={errors.general} />
                        )}
                        {apiError && (
                            <NotificationAlert type="error" message={apiError} />
                        )}

                        <Row className="mb-4">
                            <label htmlFor="title" className="col-sm-3 col-form-label">{t("shared.title", "Title")}</label>
                            <Col sm={9}>
                                <input
                                    type="text"
                                    name="title"
                                    id="title"
                                    className="form-control"
                                    value={selectedEntry?.title || ''}
                                    onChange={(e) => setSelectedEntry({...selectedEntry, title: e.target.value})}
                                    placeholder={t("shared.enter_title", "Enter title...")}
                                />
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <label htmlFor="dueDate" className="col-sm-3 col-form-label">{t("shared.due_date", "Due Date")} *</label>
                            <Col sm={9}>
                                <DatePicker
                                    id="dueDate"
                                    selected={selectedEntry?.dueDate ? new Date(selectedEntry.dueDate) : null}
                                    onChange={(date) => {
                                        if (date) {
                                            const formattedDate = formatDateTimeForAPI(date);
                                            setSelectedEntry({
                                                ...selectedEntry,
                                                dueDate: formattedDate
                                            });
                                        }
                                    }}
                                    className="form-control"
                                    dateFormat="MMMM d, yyyy"
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    timeIntervals={15}
                                    timeCaption="time"
                                    isClearable
                                    placeholderText={t("shared.select_due_date", "Select due date")}
                                />
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <label htmlFor="assetId" className="col-sm-3 col-form-label">{t("shared.asset", "Asset")} <span className="text-danger">*</span></label>
                            <Col sm={9}>
                                <div className="d-flex gap-2">
                                    <div className="flex-grow-1 position-relative">
                                        {selectedAssetDisplay ? (
                                            <div className="d-flex">
                                                <div
                                                    className="form-control d-flex align-items-center justify-content-between"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setSelectedAssetDisplay("");
                                                        setSelectedEntry({
                                                            ...selectedEntry,
                                                            asset: null,
                                                            assetId: '',
                                                            usageStatus: ''
                                                        });
                                                    }}
                                                >
                                                    <span>{selectedAssetDisplay}</span>
                                                    <FeatherIcon icon="x" size={16} className="text-muted" />
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <Input
                                                    type="text"
                                                    id="assetSearch"
                                                    placeholder={t("shared.type_to_search_assets", "Type to search assets...")}
                                                    value={assetSearchTerm}
                                                    onChange={handleAssetSearchChange}
                                                    onFocus={() => {
                                                        if (assetSearchTerm.length > 0) {
                                                            setShowAssetDropdown(true);
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        // Delay hiding dropdown to allow clicks on items
                                                        setTimeout(() => setShowAssetDropdown(false), 200);
                                                    }}
                                                    className={errors.assetId ? 'is-invalid' : ''}
                                                />
                                                {showAssetDropdown && assets.length > 0 && (
                                                    <div
                                                        className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm"
                                                        style={{
                                                            zIndex: 1050,
                                                            maxHeight: '200px',
                                                            overflowY: 'auto',
                                                            top: '100%'
                                                        }}
                                                    >
                                                        {assets.map((asset) => (
                                                            <div
                                                                key={asset.id}
                                                                className="p-2 border-bottom cursor-pointer hover-bg-light d-flex align-items-center"
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => handleAssetSelect(asset)}
                                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                                            >
                                                                <div className="flex-grow-1">
                                                                    <div className="fw-medium text-dark">{asset.assetName}</div>
                                                                    <small className="text-muted">
                                                                        {asset.serialNumber}
                                                                    </small>
                                                                </div>
                                                                <FeatherIcon icon="chevron-right" size={16} className="text-muted" />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {showAssetDropdown && assetSearchTerm.length > 0 && assets.length === 0 && (
                                                    <div
                                                        className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm p-3 text-center text-muted"
                                                        style={{
                                                            zIndex: 1050,
                                                            top: '100%'
                                                        }}
                                                    >
                                                        {t("shared.no_assets_matching", "No assets found matching")} "{assetSearchTerm}"
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <div className="align-self-end">
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary"
                                            onClick={() => setAssetFilterModal(true)}
                                            title={t("shared.filter_assets", "Filter assets")}
                                            style={{ height: '38px' }}
                                        >
                                            <FeatherIcon icon="filter" size={16} />
                                        </button>
                                    </div>
                                </div>
                                {errors.assetId && <small className="text-danger">{errors.assetId}</small>}
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <label htmlFor="description" className="col-sm-3 col-form-label">{t("shared.description", "Description")}</label>
                            <Col sm={9}>
                                <textarea
                                    name="description"
                                    id="description"
                                    className="form-control"
                                    rows="3"
                                    value={selectedEntry?.description || ''}
                                    onChange={(e) => setSelectedEntry({...selectedEntry, description: e.target.value})}
                                    placeholder={t("shared.enter_description", "Enter description")}
                                />
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <label htmlFor="comments" className="col-sm-3 col-form-label">{t("shared.comments", "Comments")}</label>
                            <Col sm={9}>
                                <textarea
                                    name="comments"
                                    id="comments"
                                    className="form-control"
                                    rows="3"
                                    value={selectedEntry?.comments || ''}
                                    onChange={(e) => setSelectedEntry({...selectedEntry, comments: e.target.value})}
                                    placeholder={t("shared.enter_comment", "Enter comment...")}
                                />
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <label htmlFor="assignedTechnician" className="col-sm-3 col-form-label">{t("shared.assigned_technicians", "Assigned Technicians")} <span className="text-danger">*</span></label>
                            <Col sm={9}>
                                <div className="mb-3">
                                    <select
                                        id="assignedTechnician"
                                        className={`form-select ${errors.assignedTechnicianIds ? 'is-invalid' : ''}`}
                                        value=""
                                        onChange={(e) => {
                                            if (e.target.value) {
                                                const selectedTech = technicians.find(tech => tech.id === Number(e.target.value));
                                                if (selectedTech) {
                                                    const currentTechnicians = selectedEntry?.assignedTechnicians || [];
                                                    const techExists = currentTechnicians.some(tech => tech.id === selectedTech.id);
                                                    if (!techExists) {
                                                        setSelectedEntry({
                                                            ...selectedEntry,
                                                            assignedTechnicians: [...currentTechnicians, selectedTech]
                                                        });
                                                    }
                                                }
                                            }
                                        }}
                                    >
                                        <option value="">{t("shared.select_technician_to_add", "Select technician to add")}</option>
                                        {technicians
                                            ?.filter(tech => !selectedEntry?.assignedTechnicians?.some(assigned => assigned.id === tech.id))
                                            .map((tech) => (
                                                <option key={tech.id} value={tech.id}>
                                                    {tech.name}
                                                </option>
                                            ))}
                                    </select>
                                    {errors.assignedTechnicianIds && (
                                        <div className="invalid-feedback d-block">
                                            {errors.assignedTechnicianIds}
                                        </div>
                                    )}
                                </div>

                                {/* Display selected technicians as badges */}
                                <div className="d-flex flex-wrap gap-2">
                                    {selectedEntry?.assignedTechnicians?.map((technician, index) => (
                                        <span
                                            key={technician.id || index}
                                            className="badge bg-primary d-flex align-items-center gap-1"
                                            style={{ fontSize: '12px', padding: '6px 10px' }}
                                        >
                                            <FeatherIcon icon="user" size={12} />
                                            {technician.name}
                                            <button
                                                type="button"
                                                className="btn-close btn-close-white ms-1"
                                                style={{ fontSize: '8px' }}
                                                onClick={() => {
                                                    const updatedTechnicians = selectedEntry.assignedTechnicians.filter((_, i) => i !== index);
                                                    setSelectedEntry({
                                                        ...selectedEntry,
                                                        assignedTechnicians: updatedTechnicians
                                                    });
                                                }}
                                                aria-label={t("shared.remove", "Remove")}
                                            />
                                        </span>
                                    ))}
                                </div>
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <label htmlFor="routineStatus" className="col-sm-3 col-form-label">{t("shared.status", "Status")}</label>
                            <Col sm={9}>
                                <select
                                    name="routineStatus"
                                    id="routineStatus"
                                    className="form-select"
                                    value={selectedEntry?.status || 'SUBMITTED'}
                                    onChange={(e) => setSelectedEntry({...selectedEntry, status: e.target.value})}
                                >
                                    {workstatusOptions(t).map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </Col>
                        </Row>

                        {selectedEntry?.assetId && (
                        <AssetUsageStatusFields
                            usageStatus={selectedEntry?.usageStatus}
                            downtimeReasonId={selectedEntry?.downtimeReasonId}
                            onChange={({ usageStatus, downtimeReasonId }) => setSelectedEntry({...selectedEntry, usageStatus, downtimeReasonId})}
                            errors={errors}
                            layout="horizontal"
                            required
                        />
                        )}

                        <Row className="mb-4">
                            <label htmlFor="checklist" className="col-sm-3 col-form-label">{t("shared.checklist", "Checklist")}</label>
                            <Col sm={9}>
                                {selectedEntry?.checkLists && selectedEntry.checkLists.map((item, index) => (
                                    <div key={index} className="d-flex gap-2 mb-2 align-items-center">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={item.checkListName || ''}
                                            onChange={(e) => {
                                                const updatedCheckLists = [...(selectedEntry.checkLists || [])];
                                                updatedCheckLists[index] = {
                                                    ...updatedCheckLists[index],
                                                    checkListName: e.target.value
                                                };
                                                setSelectedEntry({
                                                    ...selectedEntry,
                                                    checkLists: updatedCheckLists
                                                });
                                            }}
                                            placeholder={t("business.routine.checklist_item_placeholder", "Checklist item {{count}}", { count: index + 1 })}
                                        />
                                        {index === selectedEntry.checkLists.length - 1 && (
                                            <button
                                                type="button"
                                                className="btn btn-success d-flex align-items-center justify-content-center"
                                                onClick={() => {
                                                    setSelectedEntry({
                                                        ...selectedEntry,
                                                        checkLists: [...(selectedEntry.checkLists || []), { checkListName: '', isChecked: false }]
                                                    });
                                                }}
                                                style={{ minWidth: '38px', height: '38px' }}
                                            >
                                                <FeatherIcon icon="plus" size={16} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                {(!selectedEntry?.checkLists || selectedEntry.checkLists.length === 0) && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-success d-flex align-items-center gap-2"
                                        onClick={() => {
                                            setSelectedEntry({
                                                ...selectedEntry,
                                                checkLists: [{ checkListName: '', isChecked: false }]
                                            });
                                        }}
                                    >
                                        <FeatherIcon icon="plus" size={16} />
                                        <span>{t("business.routine.add_checklist_item", "Add Checklist Item")}</span>
                                    </button>
                                )}
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <label htmlFor="preImagesUpload" className="col-sm-3 col-form-label">{t("shared.pre_images", "Pre Images")}</label>
                            <Col sm={9}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleFileChange(e, 'preImages')}
                                    className="form-control"
                                    id="preImagesUpload"
                                    multiple
                                />
                                {/* Display uploaded images as thumbnails */}
                                {selectedEntry?.preImages && selectedEntry.preImages.length > 0 && (
                                    <div className="mt-3">
                                        <div className="d-flex flex-wrap gap-2">
                                            {selectedEntry.preImages.map((imageUrl, index) => (
                                                <div key={index} className="position-relative">
                                                    <img
                                                        src={imageUrl}
                                                        alt={t("business.routine.pre_image_alt", "Pre Image {{count}}", { count: index + 1 })}
                                                        className="img-thumbnail"
                                                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger position-absolute top-0 end-0"
                                                        style={{ transform: 'translate(50%, -50%)', padding: '2px 6px' }}
                                                        onClick={() => removeImage(index, 'preImages')}
                                                    >
                                                        <FeatherIcon icon="x" size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <label htmlFor="postImagesUpload" className="col-sm-3 col-form-label">{t("shared.post_images", "Post Images")}</label>
                            <Col sm={9}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleFileChange(e, 'postImages')}
                                    className="form-control"
                                    id="postImagesUpload"
                                    multiple
                                />
                                {/* Display uploaded images as thumbnails */}
                                {selectedEntry?.postImages && selectedEntry.postImages.length > 0 && (
                                    <div className="mt-3">
                                        <div className="d-flex flex-wrap gap-2">
                                            {selectedEntry.postImages.map((imageUrl, index) => (
                                                <div key={index} className="position-relative">
                                                    <img
                                                        src={imageUrl}
                                                        alt={t("business.routine.post_image_alt", "Post Image {{count}}", { count: index + 1 })}
                                                        className="img-thumbnail"
                                                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger position-absolute top-0 end-0"
                                                        style={{ transform: 'translate(50%, -50%)', padding: '2px 6px' }}
                                                        onClick={() => removeImage(index, 'postImages')}
                                                    >
                                                        <FeatherIcon icon="x" size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </div>
                    <div className="modal-footer">
                        <Button type="button" color="secondary" onClick={addEditModal.close}>
                            {t("shared.cancel", "Cancel")}
                        </Button>
                        <Button type="submit" color="primary">
                            {selectedEntry?.id ? t("shared.update", 'Update') : t("shared.save", 'Save')}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <ConfirmDeleteModal
                isOpen={deleteModal.isOpen}
                toggle={deleteModal.close}
                onConfirm={handleDelete}
                message={t("business.routine.delete_confirmation", "Are you sure you want to delete this routine request? This action cannot be undone.")}
            />

            {/* Image Modal for full-screen view */}
            <AnimatePresence>
                {showImageModal && currentImages.length > 0 && (
                    <Modal
                        isOpen={showImageModal}
                        toggle={closeImageModal}
                        size="lg"
                        centered
                        className="image-modal"
                        backdrop="static"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ type: "spring", stiffness: 300, damping: 25 }}
                            className="modal-content border-0"
                            style={{ backgroundColor: 'rgba(0,0,0,0.9)' }}
                        >
                            <div className="modal-header border-0 bg-transparent text-white">
                                <h5 className="modal-title">
                                    {imageType === 'pre' ? t("shared.pre", 'Pre') : t("shared.post", 'Post')} {t("shared.image", "Image")} {selectedImageIndex + 1} {t("shared.of", "of")} {currentImages.length}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={closeImageModal}
                                    aria-label={t("shared.close", "Close")}
                                />
                            </div>

                            <div className="modal-body p-0 d-flex align-items-center justify-content-center position-relative">
                                {/* Navigation Arrows */}
                                {currentImages.length > 1 && (
                                    <>
                                        <button
                                            className="btn btn-outline-light position-absolute start-0 ms-3 rounded-circle"
                                            style={{ zIndex: 10, width: '50px', height: '50px' }}
                                            onClick={prevImage}
                                        >
                                            <i className="bx bx-chevron-left fs-4"></i>
                                        </button>
                                        <button
                                            className="btn btn-outline-light position-absolute end-0 me-3 rounded-circle"
                                            style={{ zIndex: 10, width: '50px', height: '50px' }}
                                            onClick={nextImage}
                                        >
                                            <i className="bx bx-chevron-right fs-4"></i>
                                        </button>
                                    </>
                                )}

                                {/* Main Image */}
                                <motion.img
                                    key={selectedImageIndex}
                                    src={currentImages[selectedImageIndex]}
                                    alt={`${imageType === 'pre' ? t("shared.pre", 'Pre') : t("shared.post", 'Post')} ${t("shared.image", "Image")} ${selectedImageIndex + 1}`}
                                    className="img-fluid"
                                    style={{
                                        maxHeight: '70vh',
                                        maxWidth: '100%',
                                        objectFit: 'contain'
                                    }}
                                    initial={{ opacity: 0, x: 100 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -100 }}
                                    transition={{ duration: 0.3 }}
                                    onError={(e) => {
                                    }}
                                />
                            </div>

                            {/* Image Navigation Dots */}
                            {currentImages.length > 1 && (
                                <div className="modal-footer border-0 bg-transparent d-flex justify-content-center">
                                    <div className="d-flex gap-2">
                                        {currentImages.map((_, index) => (
                                            <button
                                                key={index}
                                                className={`btn rounded-circle p-1 ${
                                                    index === selectedImageIndex
                                                        ? 'btn-light'
                                                        : 'btn-outline-light'
                                                }`}
                                                style={{ width: '12px', height: '12px' }}
                                                onClick={() => setSelectedImageIndex(index)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </Modal>
                )}
            </AnimatePresence>

            {/* Work Order Details Modal */}
            <WorkOrderDetailsModal
                isOpen={workOrderModal.isOpen}
                toggle={workOrderModal.close}
                workOrder={workOrderModal.data}
                workType="routine"
                onEdit={(workOrder) => {
                    workOrderModal.close();
                    handleEdit(workOrder);
                }}
                onChecklistUpdate={async (data) => {
                    try {
                        if (data.action === 'delete') {
                            // Delete checklist item
                            await api.delete(`routine-requests/deleteCheckList?id=${data.id}`);
                        } else {
                            // Update checklist item
                            await api.put(`routine-requests/updateCheckList`, {
                                id: data.id,
                                checkListName: data.checkListName,
                                isChecked: data.checked,
                                remarks: data.remarks || ''
                            });
                        }

                        // Fetch fresh data for this specific work order
                        const { data: response } = await api.get(`routine-requests/getAllRoutineRequest?organizationId=${organizationId || 1}&count=${count}`);
                        const updatedRequests = response?.response || [];
                        setRoutineRequests(updatedRequests);

                        // Find and update the selected work order
                        const updatedWorkOrder = updatedRequests.find(r => r.id === workOrderModal.data?.id);
                        if (updatedWorkOrder) {
                            workOrderModal.setData({ ...updatedWorkOrder, type: 'routine' });
                        }
                    } catch (error) {
                        console.error('Error updating checklist:', error);
                        alert(t("business.routine.failed_to_update_checklist", 'Failed to update checklist item'));
                    }
                }}
            />

            {/* Asset Modal */}
            <AssetModal
                isOpen={assetModal.isOpen}
                toggle={() => setAssetModal(prev => ({ ...prev, isOpen: false }))}
                userData={assetModal.userData}
                loading={assetModal.loading}
                formatCreatedDate={formatCreatedDate}
            />

            {/* Asset Filter Modal */}
            <Modal
                isOpen={assetFilterModal}
                toggle={() => setAssetFilterModal(false)}
                centered
            >
                <div className="modal-header">
                    <h5 className="modal-title">{t("shared.filter_assets", "Filter Assets")}</h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setAssetFilterModal(false)}
                        aria-label={t("shared.close", "Close")}
                    />
                </div>

                <div className="modal-body">
                    <Row className="mb-3">
                        <Col md={12}>
                            <label htmlFor="assetFilterLocation" className="form-label">
                                {t("shared.location", "Location")}
                            </label>
                            <select
                                id="assetFilterLocation"
                                className="form-select"
                                value={assetFilters.location}
                                onChange={(e) =>
                                    setAssetFilters({ ...assetFilters, location: e.target.value })
                                }
                            >
                                <option value="">{t("shared.all_locations", "All Locations")}</option>
                                {locations?.map((location) => (
                                    <option key={location.id} value={location.locationName}>
                                        {location.locationName}
                                    </option>
                                ))}
                            </select>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={12}>
                            <label htmlFor="assetFilterManufacturer" className="form-label">
                                {t("shared.manufacturer", "Manufacturer")}
                            </label>
                            <select
                                id="assetFilterManufacturer"
                                className="form-select"
                                value={assetFilters.manufacturer}
                                onChange={(e) =>
                                    setAssetFilters({ ...assetFilters, manufacturer: e.target.value })
                                }
                            >
                                <option value="">{t("shared.all_manufacturers", "All Manufacturers")}</option>
                                {manufacturers?.map((manufacturer) => (
                                    <option key={manufacturer} value={manufacturer}>
                                        {manufacturer}
                                    </option>
                                ))}
                            </select>
                        </Col>
                    </Row>
                </div>

                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                            setAssetFilters({
                                location: '',
                                manufacturer: ''
                            });
                            setAssetFilterModal(false);
                        }}
                    >
                        {t("shared.clear_filters", "Clear Filters")}
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setAssetFilterModal(false)}
                    >
                        {t("shared.apply_filters", "Apply Filters")}
                    </button>
                </div>
            </Modal>

        </div>
    );
};

export default Routine;